import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CampaignIcon from "@mui/icons-material/Campaign";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import "../../styles/Listing/listing-premium.css";
import "../../styles/CampaignLeads/campaign-leads-drawer.css";
import { postRequest } from "../../api/apiServices";
import { compaignFormList } from "../../api/apiList";
import Loader from "../../utils/Loading";
import PaginationComponent from "../../utils/PaginationComponent";
import CampaignLeadsDrawer, {
  CampaignLeadsDrawerCampaign,
} from "../../components/CampaignLeads/CampaignLeadsDrawer";

interface Campaign {
  id: number;
  name: string;
  campaignId: string;
  campaignStatus?: string;
}

const CandidateMenu = () => {
  const [campaign, setCampaign] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loader, setLoader] = useState(false);
  const [leadsDrawerCampaign, setLeadsDrawerCampaign] =
    useState<CampaignLeadsDrawerCampaign | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tableRef = useRef(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openLeadsDrawer = (item: Campaign) => {
    setLeadsDrawerCampaign({
      campaignId: item.campaignId,
      name: item.name,
    });
    setDrawerOpen(true);
  };

  const closeLeadsDrawer = () => {
    setDrawerOpen(false);
    setLeadsDrawerCampaign(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchCompaignData(page, rowsPerPage, value);
    }, 300);
  };

  const fetchCompaignData = async (
    pageNum: number,
    limit: number,
    search: string
  ) => {
    setLoader(true);
    const response = await postRequest(compaignFormList, {
      page: pageNum,
      limit,
      search,
      searchColumns: ["campaignName"],
    });

    if (response?.data?.success) {
      const apiData = response.data.data;

      setCampaign(
        (apiData?.campaigns || []).map((c: any) => ({
          id: c.id,
          name: c.campaignName,
          campaignId: c.campaignId,
          campaignStatus: c.campaignStatus,
        }))
      );
      setCount(apiData?.count || 0);
      setPage((apiData?.page || 1) - 1);
      setHasNextPage(apiData?.hasNextPage || false);
      setHasPreviousPage(apiData?.hasPreviousPage || false);
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchCompaignData(page + 1, rowsPerPage, searchTerm);
  }, [page, rowsPerPage]);

  const getStatusClass = (status?: string) => {
    const normalized = status?.toLowerCase() || "";
    if (normalized === "active") return "listing-status-chip--active";
    if (normalized === "completed") return "listing-status-chip--default";
    return "listing-status-chip--warning";
  };

  return (
    <>
      <Box className="listing-page">
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <CampaignIcon sx={{ fontSize: 14 }} />
                Campaigns
              </Box>
              <h1 className="listing-hero-title">Campaign Management</h1>
              <p className="listing-hero-subtitle">
                Manage and track all your recruitment campaigns
              </p>
            </Box>

            <Box className="listing-hero-search">
              <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Box>
          </Box>
        </Box>

        <Box ref={tableRef}>
          {loader ? (
            <Loader />
          ) : campaign.length === 0 ? (
            <Box className="listing-empty">
              <Box className="listing-empty-icon">
                <CampaignIcon />
              </Box>
              <h2 className="listing-empty-title">No Campaigns Yet</h2>
              <p className="listing-empty-text">
                {searchTerm
                  ? `No campaigns found matching "${searchTerm}".`
                  : "Start by creating your first campaign to see candidates and manage your recruitment process."}
              </p>
            </Box>
          ) : (
            <>
              <Box className="listing-list">
                {campaign.map((item, index) => (
                  <Box key={item.id} className="listing-card">
                    <Box className="listing-card-body">
                      <Box className="listing-card-info">
                        <Box className="listing-card-title-row">
                          <Box className="listing-card-index">
                            {page * rowsPerPage + index + 1}
                          </Box>
                          <Box>
                            <p className="listing-card-title">
                              {item.name || "N/A"}
                            </p>
                            <Box className="listing-card-subtitle">
                              {item.campaignId || "-"}
                            </Box>
                          </Box>
                        </Box>
                        <span
                          className={`listing-status-chip ${getStatusClass(item.campaignStatus)}`}
                        >
                          {item.campaignStatus || "Pending"}
                        </span>
                      </Box>

                      <button
                        type="button"
                        className="campaign-leads-view-btn"
                        onClick={() => openLeadsDrawer(item)}
                      >
                        <PeopleOutlineIcon sx={{ fontSize: 18 }} />
                        View Candidates
                      </button>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box className="listing-pagination-wrap">
                <PaginationComponent
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <CampaignLeadsDrawer
        open={drawerOpen}
        campaign={leadsDrawerCampaign}
        onClose={closeLeadsDrawer}
      />
    </>
  );
};

export default CandidateMenu;
